import { View, Text, StyleSheet } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { Colors, SELECT_STYLES } from "@/constants";
import PrimaryButton from "./PrimaryButton";
import { useState } from "react";
import { useUserSlice } from "@/models/useUserSlice";
import Input from "./Input";
import { getBasePath, getFormattedDate } from "@/models/utils";
import { useRouter } from "@/_router";

const AlertWidgetCreateNoteText = () => {
  const visible = useUISlice((state) => state.alertWidget === "text");
  const setVisible = useUISlice((state) => state.setAlertWidget);
  const router = useRouter();
  const width = useUISlice((state) => state.device.width);
  const [text, setText] = useState<string>("");
  const { t } = useTranslation();

  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const folders = useUserSlice((state) => {
    if (state?.folders?.length === 0) return undefined;
    return [
      { id: undefined, name: t("alertWidgetCreateNoteText.allNotes") },
      ...state.folders,
    ];
  });

  if (!visible) return null;

  const onPress = async () => {
    if (!text.trim()) return;

    setVisible(undefined);
    setText("");

    // Split text by line breaks and create transcript segments
    const trimmedText = text.trim();
    // Handle multiple consecutive line breaks by treating them as one
    const lines = trimmedText.split(/\r?\n+/).filter((line) => line.length > 0);

    // Process the transcript segments
    let position = 0;
    const transcript = lines.map((line) => {
      const segment = {
        text: line,
        start: position,
        end: position + 1,
      };
      // Update position for next segment, accounting for the newline character
      position = segment.end + 1;
      return segment;
    });

    useUserSlice
      .getState()
      .note()
      .create(
        {
          type: "text",
          name: `Copied text-${getFormattedDate()}`,
          transcript:
            transcript.length > 0
              ? transcript
              : [{ text: trimmedText, start: 0, end: trimmedText.length }],
          status: "transcription_completed",
        },
        folderId,
        (note) => {
          router.push(`${getBasePath()}/note?id=${note.id}`);
        }
      );
  };

  return (
    <ModalContainer
      title={t("alertWidgetCreateNoteText.title")}
      onDismiss={() => setVisible(undefined)}
      hideCancel
      modalWidth={Math.min(width, 600)}
    >
      <Text style={styles.label}>{t("alertWidgetCreateNoteText.text")}</Text>
      <Input
        value={text}
        onTextChange={setText}
        placeholder={t("alertWidgetCreateNoteText.placeholder")}
        multiLine
      />
      <View style={styles.spacer} />
      {folders && (
        <>
          <View style={styles.spacer} />
          <Text style={styles.label}>
            {t("alertWidgetCreateNoteText.folder")}
          </Text>
          <select
            style={SELECT_STYLES}
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
          >
            {folders.map((folder, index) => (
              <option key={index} value={folder.id}>
                {`📂 ${folder.name}`}
              </option>
            ))}
          </select>
        </>
      )}
      <View style={styles.largerSpacer} />
      <PrimaryButton
        icon="upload"
        text={t("alertWidgetCreateNoteText.submit")}
        onPress={onPress}
      />
      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: ms(16),
  },
  largerSpacer: {
    height: ms(48),
  },
  label: {
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text,
    marginBottom: ms(8),
  },
  negativeMargin: {
    marginBottom: -ms(16),
  },
});

export default AlertWidgetCreateNoteText;
